import type * as React from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';

import { cn } from '../../styles/lib/utils';
import { Select, type SelectOption } from '../Select';
import { FormControl, FormField, type FormFieldProps, FormItem, FormMessage } from './Form';

type FormSelectProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  FormFieldProps<TFieldValues, TName>,
  'render' | 'disabled'
> & {
  label: string;
  options: SelectOption[];
  placeholder?: string;
  tooltipText?: React.ReactNode;
  isClearable?: boolean;
  disabled?: boolean;
  secondaryAction?: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  'data-testid'?: string;
  labelTestId?: string;
};

/**
 * react-hook-form field around `Select`, for a short, fixed list of roughly 10 options or fewer. Long or data-driven
 * lists use `FormComboBox`; see the `Select` doc block for the rule. `FormControl` hands `Select` the field id, so
 * `Select`'s own label, the control, and the error message share the form field's ids.
 */
function FormSelect<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  label,
  options,
  placeholder = 'Select...',
  tooltipText,
  isClearable = false,
  disabled,
  secondaryAction,
  className,
  triggerClassName,
  'data-testid': testId,
  labelTestId,
  ...fieldProps
}: FormSelectProps<TFieldValues, TName>) {
  return (
    <FormField
      {...fieldProps}
      render={({ field }) => (
        <FormItem className={className}>
          <FormControl>
            <Select
              label={label}
              isRequired={fieldProps.isRequired}
              tooltipText={tooltipText}
              secondaryAction={secondaryAction}
              labelTestId={labelTestId}
              options={options}
              value={field.value ?? ''}
              onValueChange={(next) => field.onChange(next)}
              onBlur={field.onBlur}
              placeholder={placeholder}
              isClearable={isClearable}
              disabled={disabled}
              triggerClassName={cn('w-full', triggerClassName)}
              data-testid={testId}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export { FormSelect };
export type { FormSelectProps };
